import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { EVENT_CATEGORY_OPTIONS } from 'hooks/orgEvents/orgEventAutomationHooks';
import { SimpleSelect } from './automationFormControls';
import { CUSTOM_CATEGORY_PRESET, SEVERITY_OPTIONS } from './eventCategory';

/** One segmented control. Color and an inset border mark the selected segment. */
const SEVERITY_TOGGLE_CLASS = {
  High: 'tw:text-destructive tw:hover:!bg-destructive/10 tw:hover:!text-destructive tw:aria-pressed:!bg-destructive tw:aria-pressed:!text-destructive-foreground',
  Medium:
    'tw:text-amber-800 tw:hover:!bg-amber-400/25 tw:hover:!text-amber-900 tw:aria-pressed:!bg-amber-500 tw:aria-pressed:!text-amber-950 tw:dark:text-amber-200 tw:dark:hover:!text-amber-100',
  Low: 'tw:text-blue-800 tw:hover:!bg-blue-500/15 tw:hover:!text-blue-900 tw:aria-pressed:!bg-blue-600 tw:aria-pressed:!text-white tw:dark:text-blue-200 tw:dark:hover:!text-blue-100',
} as const;

export function CategoryFields({
  preset,
  custom,
  onPresetChange,
  onCustomChange,
  customInputId,
}: {
  preset: string;
  custom: string;
  onPresetChange: (next: string) => void;
  onCustomChange: (next: string) => void;
  customInputId: string;
}) {
  const showCustom = preset === CUSTOM_CATEGORY_PRESET;

  return (
    <Field>
      <FieldLabel>Category</FieldLabel>
      <SimpleSelect
        value={preset}
        onChange={onPresetChange}
        options={[...EVENT_CATEGORY_OPTIONS]}
      />
      {showCustom ? (
        <>
          <FieldLabel htmlFor={customInputId} className="tw:sr-only">
            Custom category
          </FieldLabel>
          <Input
            id={customInputId}
            value={custom}
            placeholder="Category name"
            onChange={(event) => onCustomChange(event.target.value)}
          />
          <FieldDescription>
            This name is saved as the category.
          </FieldDescription>
        </>
      ) : null}
    </Field>
  );
}

export function SeverityToggle({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const selected = SEVERITY_OPTIONS.includes(
    value as (typeof SEVERITY_OPTIONS)[number]
  )
    ? value
    : 'Medium';

  return (
    <Field>
      <FieldLabel>Severity</FieldLabel>
      <ToggleGroup
        variant="outline"
        spacing={0}
        multiple={false}
        value={[selected]}
        onValueChange={(next) => {
          const picked = next[0];
          if (picked) onChange(picked);
        }}
        aria-label="Severity"
        className="tw:justify-start"
      >
        {SEVERITY_OPTIONS.map((option) => (
          <ToggleGroupItem
            key={option}
            value={option}
            className={`tw:bg-transparent tw:px-4 tw:shadow-none tw:aria-pressed:!border-border tw:aria-pressed:!ring-2 tw:aria-pressed:!ring-inset tw:aria-pressed:!ring-foreground ${SEVERITY_TOGGLE_CLASS[option]}`}
          >
            {option}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Field>
  );
}
